/**
 * The user's plan, as every screen asks about it.
 *
 * `useLicense()` is the one hook Apply buttons use: `requireApply(run)` runs
 * `run` when the cached subscription allows applying and opens the upgrade
 * dialog instead when it does not. The server has the last word either way:
 * a 402 from any apply endpoint opens the same dialog (see store/license.ts).
 */

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { billingApi } from '@/api/endpoints'
import { isLicenseError, useLicenseStore, type LicensePrompt } from '@/store/license'
import { toast } from '@/store/toast'
import type { BillingPeriod, CheckoutRequest, PlanFeatures, PlanKey, SubscriptionResponse } from '@/types/api'

import { NEW_SUBSCRIPTIONS_ONLY, useCouponStore } from './coupon'

export const SUBSCRIPTION_KEY = ['billing', 'subscription'] as const
export const PLANS_KEY = ['billing', 'plans'] as const

export function useSubscription() {
  return useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: billingApi.subscription,
    staleTime: 20_000,
    retry: false,
  })
}

export function usePlans() {
  return useQuery({
    queryKey: PLANS_KEY,
    queryFn: billingApi.plans,
    staleTime: 10 * 60_000,
  })
}

/** After anything that spends an application: the chip and the meter catch up. */
export function refreshLicense(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
}

/** Why the cached subscription says no, in the dialog's terms. */
export function promptFor(data: SubscriptionResponse): LicensePrompt {
  return {
    reason: data.reason ?? (data.subscription ? 'subscription_inactive' : 'subscription_required'),
    plan: data.subscription?.plan ?? null,
    remaining: data.usage?.remaining ?? null,
    windowEnd: data.usage?.windowEnd ?? null,
    limit: data.usage?.limit ?? null,
  }
}

export function useLicense() {
  const query = useSubscription()
  const open = useLicenseStore((state) => state.open)
  const data = query.data ?? null

  /** Runs `onOk` when applying is allowed (or not known yet: the server decides); otherwise opens the dialog. */
  const requireApply = useCallback(
    (onOk: () => void): boolean => {
      if (data && !data.canApply) {
        open(promptFor(data))
        return false
      }
      onOk()
      return true
    },
    [data, open],
  )

  /** Runs `onOk` when the plan includes `feature`; otherwise opens the dialog for it. */
  const requireFeature = useCallback(
    (feature: keyof PlanFeatures, onOk: () => void): boolean => {
      if (data && !data.features[feature]) {
        open(
          data.active
            ? { reason: 'feature_not_in_plan', feature, plan: data.subscription?.plan ?? null }
            : promptFor(data),
        )
        return false
      }
      onOk()
      return true
    },
    [data, open],
  )

  return {
    /** The whole response, or null while loading / when it failed. */
    subscription: data,
    isLoading: query.isLoading,
    /** True until the server says otherwise. */
    canApply: data ? data.canApply : true,
    /** Null until loaded. */
    features: data?.features ?? null,
    requireApply,
    requireFeature,
  }
}

/** A paid (Stripe) subscription is in force: coupon codes do not apply to changing it. */
export function hasPaidPlan(data: SubscriptionResponse | null | undefined): boolean {
  return Boolean(data?.active && data.subscription?.source === 'stripe')
}

/**
 * Start checkout for a plan: Stripe for a new subscription, or an in-place plan change.
 * A valid coupon (features/billing/coupon.ts) goes along as `couponCode`, except for a
 * plan change of a paid subscription, which codes do not cover.
 */
export function useCheckout() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (body: CheckoutRequest) => billingApi.checkout(body),
    onSuccess: (result) => {
      if (result.url) {
        window.location.href = result.url
        return
      }
      if (result.subscription) queryClient.setQueryData(SUBSCRIPTION_KEY, result.subscription)
      refreshLicense(queryClient)
      if (result.changed) {
        const name = result.subscription?.subscription?.planName
        toast.success('Plan changed', name ? `You are now on ${name}.` : undefined)
      } else {
        toast.info('Nothing changed', 'You are already on this plan.')
      }
      useLicenseStore.getState().close()
    },
    onError: (error: ApiError) => {
      if (isLicenseError(error)) return
      if (error instanceof ApiError && error.code === 'coupon_new_subscriptions_only') {
        useCouponStore.getState().setError(NEW_SUBSCRIPTIONS_ONLY)
        toast.error('Coupon not applied', 'Codes work for new subscriptions. Remove the code to change your current plan.')
        return
      }
      if (error instanceof ApiError && error.code.startsWith('coupon')) {
        // The code stopped working between Apply and checkout (deactivated, used up, expired).
        useCouponStore.setState({ applied: null, error: error.message })
        toast.error('Coupon not applied', `${error.message} Remove it or try another code, then choose the plan again.`)
        return
      }
      toast.error('Could not start checkout', error.message)
    },
  })
  const choose = (plan: PlanKey, period: BillingPeriod) => {
    const coupon = useCouponStore.getState().applied
    const subscription = queryClient.getQueryData<SubscriptionResponse>(SUBSCRIPTION_KEY)
    const couponCode = coupon && !hasPaidPlan(subscription) ? coupon.code : undefined
    mutation.mutate({ plan, period, ...(couponCode && { couponCode }) })
  }
  return {
    choose,
    /** The plan whose checkout is starting (its button spins). */
    pendingPlan: mutation.isPending ? mutation.variables?.plan ?? null : null,
    // Stays "busy" after a redirect URL arrives, until the browser leaves.
    busy: mutation.isPending || Boolean(mutation.data?.url),
  }
}

/** Stripe's billing page: card, invoices, cancel. */
export function useBillingPortal() {
  const mutation = useMutation({
    mutationFn: billingApi.portal,
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: (error: ApiError) => toast.error('Could not open billing', error.message),
  })
  return { open: () => mutation.mutate(), pending: mutation.isPending || Boolean(mutation.data?.url) }
}
